// services/logActivity.js — запись событий активности пользователей в Firestore

import {
  collection,
  addDoc,
  doc,
  updateDoc,
  getDocs,
  getDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Типы событий:
 *   login          — вход в аккаунт (пароль / Google / регистрация)
 *   logout         — выход
 *   session_start  — открыл приложение и начал работу (в т.ч. без повторного входа)
 *   drawing_save   — сохранение (.ptCAD, PDF, DWG, спецификация, IGES, STEP, DXF)
 *   drawing_open   — открытие чертежа из истории
 *   session_end    — закончил работу (закрыл вкладку / вышел)
 */

export async function logActivity(uid, email, type, payload = {}) {
  if (!uid) return;
  try {
    await addDoc(collection(db, 'activityLog'), {
      uid,
      email: email || '',
      type,
      ...payload,
      ts: serverTimestamp(),
    });
  } catch (e) {
    console.warn('[logActivity] failed:', e.message);
  }
}

/** Длительность сессии для статистики (без накрутки «до сейчас»). */
export function sessionDurationForStats(s) {
  if (s.endedAt) {
    if (s.durationSec != null) return s.durationSec;
    const start = s.startedAt?.toDate?.();
    const end = s.endedAt?.toDate?.();
    if (start && end) return Math.max(0, Math.round((end - start) / 1000));
  }
  if (s.durationSec != null) return s.durationSec;
  const start = s.startedAt?.toDate?.();
  const ping = s.lastPing?.toDate?.();
  if (start && ping) return Math.max(0, Math.round((ping - start) / 1000));
  return null;
}

function durationAtClose(data) {
  if (data.durationSec != null) return data.durationSec;
  const start = data.startedAt?.toDate?.();
  const ping = data.lastPing?.toDate?.();
  if (start && ping) return Math.max(0, Math.round((ping - start) / 1000));
  if (data.clientStartMs) {
    return Math.max(0, Math.round((Date.now() - data.clientStartMs) / 1000));
  }
  return 0;
}

async function endOpenSessionsForUid(uid) {
  if (!uid) return;
  try {
    const snap = await getDocs(query(
      collection(db, 'sessions'),
      where('uid', '==', uid),
      where('endedAt', '==', null),
    ));
    await Promise.all(snap.docs.map((d) => {
      const data = d.data();
      return updateDoc(d.ref, {
        endedAt: serverTimestamp(),
        durationSec: durationAtClose(data),
      });
    }));
  } catch (e) {
    console.warn('[endOpenSessionsForUid]', e.message);
  }
}

/**
 * Начать новую сессию и вернуть её ID.
 * Закрывает предыдущие незавершённые сессии этого uid.
 */
export async function startSession({ uid, email, isGuest }) {
  if (!uid) return null;
  try {
    await endOpenSessionsForUid(uid);
    const clientStartMs = Date.now();
    const sessionRef = await addDoc(collection(db, 'sessions'), {
      uid,
      email: email || '',
      isGuest: !!isGuest,
      startedAt: serverTimestamp(),
      endedAt: null,
      durationSec: null,
      clientStartMs,
    });
    await logActivity(uid, email, 'session_start', { sessionId: sessionRef.id, isGuest: !!isGuest });
    return sessionRef.id;
  } catch (e) {
    console.error('[startSession] FAILED:', e.code, e.message);
    return null;
  }
}

/**
 * Закрыть сессию в Firestore и при необходимости записать «Закончил работу» в журнал.
 * @param {object} [options] logToJournal — писать session_end (false для тихого автозакрытия)
 */
export async function finalizeSession(sessionId, durationSec, { logToJournal = true } = {}) {
  if (!sessionId) return;
  try {
    const ref = doc(db, 'sessions', sessionId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    if (data.endedAt) return;

    const finalDuration = durationSec != null ? durationSec : durationAtClose(data);
    await updateDoc(ref, {
      endedAt: serverTimestamp(),
      durationSec: finalDuration,
    });

    if (logToJournal && data.uid) {
      await logActivity(data.uid, data.email, 'session_end', {
        sessionId,
        durationSec: finalDuration,
        isGuest: !!data.isGuest,
      });
    }
  } catch (e) {
    console.warn('[finalizeSession] failed:', e.message);
  }
}

export async function endSession(sessionId, clientStartMs) {
  if (!sessionId) return;
  const durationSec = clientStartMs ? Math.round((Date.now() - clientStartMs) / 1000) : null;
  await finalizeSession(sessionId, durationSec);
}

export async function pingSession(sessionId, clientStartMs) {
  if (!sessionId) return;
  try {
    const durationSec = clientStartMs ? Math.round((Date.now() - clientStartMs) / 1000) : null;
    await updateDoc(doc(db, 'sessions', sessionId), {
      lastPing: serverTimestamp(),
      durationSec,
    });
  } catch (e) {
    // тихая ошибка
  }
}

export function getOrCreateGuestId() {
  const KEY = 'ptcad_guest_id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = 'guest_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(KEY, id);
  }
  return id;
}
